/**
 * Sentry crash reporting setup for Foresight Meet Schedule Predictor.
 *
 * Captures both JavaScript errors AND native Android crashes (via the
 * @sentry/capacitor plugin, which auto-installs the native Sentry Android
 * SDK during `npx cap sync`).
 *
 * IMPORTANT INVARIANTS:
 *   - Sentry is ONLY initialized on native Capacitor builds.
 *     The read-only public web build at app.foresightpulse.com is anonymous,
 *     handles no PII, and we do not want to consume the free-tier quota on
 *     spectator traffic.
 *   - DSN is read from REACT_APP_SENTRY_DSN. If the env var is missing, we
 *     no-op rather than crash.
 */
import { Capacitor } from '@capacitor/core';
import * as Sentry from '@sentry/capacitor';
import * as SentryReact from '@sentry/react';

let initialized = false;

export function initSentry() {
  if (initialized) return;

  // Skip on web — only native builds report crashes.
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  const dsn = process.env.REACT_APP_SENTRY_DSN;
  if (!dsn) {
    // Fail silent — better than crashing on a missing config in production.
    return;
  }

  Sentry.init(
    {
      dsn,
      environment: process.env.NODE_ENV === 'production' ? 'production' : 'development',
      // App version is set at build time; falls back to the Capacitor platform
      // name when missing so we can at least segment Android vs iOS.
      release: process.env.REACT_APP_BUILD_VERSION || `foresight-${Capacitor.getPlatform()}`,
      // Capture 10% of transactions in production, 100% in dev.
      tracesSampleRate: process.env.NODE_ENV === 'production' ? 0.1 : 1.0,
      // Don't send default PII; we have no user identifiers worth tracking and
      // it keeps the Play Store data-safety form simple.
      sendDefaultPii: false,
    },
    // Forward the same init to the React SDK so <ErrorBoundary> + hooks work.
    SentryReact.init
  );

  initialized = true;
}

export { Sentry, SentryReact };
